import type { ProductPaymentMethod } from '@/services';
import { toaster } from '@/shared/ui/toaster.component';
import { useStore } from '@tanstack/react-form';
import { useState } from 'react';
import {
  EMPTY_PAYMENT_METHOD_FORM,
  paymentMethodBankError,
  toCreatePaymentMethodPayload,
  toPaymentMethodFormValues,
  toUpdatePaymentMethodPayload,
  type PaymentMethodFormValues,
} from '../schemas/payment-method.schema';
import { BILLING_METHOD_REQUIRING_BANK } from '../utils/payment-settings-labels.util';
import { diffPaymentMethodRow } from '../utils/settings-diff.util';
import { usePaymentSettingsForm } from './use-payment-settings-form.hook';
import { useSavePaymentMethod } from './use-save-payment-method.hook';

type UsePaymentMethodFormInput = {
  productCode: string;
  /** Ausente = criar uma forma nova. */
  method?: ProductPaymentMethod;
  onDone: () => void;
};

/**
 * Formulário de uma forma do catálogo (modal). Na edição o código fica
 * travado — ele já está gravado nas apólices.
 */
export function usePaymentMethodForm({
  productCode,
  method,
  onDone,
}: UsePaymentMethodFormInput) {
  const mutation = useSavePaymentMethod(productCode);
  const [serverError, setServerError] = useState<string | null>(null);
  const [bankError, setBankError] = useState<string | undefined>();
  const isEdit = Boolean(method);

  const form = usePaymentSettingsForm({
    defaultValues: method
      ? toPaymentMethodFormValues(method)
      : EMPTY_PAYMENT_METHOD_FORM,
    onSubmit: async ({ value }: { value: PaymentMethodFormValues }) => {
      const crossFieldError = paymentMethodBankError(value);
      setBankError(crossFieldError);
      if (crossFieldError) return;

      setServerError(null);
      try {
        if (method) {
          const payload = toUpdatePaymentMethodPayload(
            value,
            method.version ?? 0
          );
          const changes = diffPaymentMethodRow(method, {
            ...payload,
            code: method.code,
          });
          if (changes.length === 0) {
            onDone();
            return;
          }
          await mutation.mutateAsync({
            kind: 'update',
            code: method.code,
            payload,
          });
        } else {
          await mutation.mutateAsync({
            kind: 'create',
            payload: toCreatePaymentMethodPayload(value),
          });
        }

        toaster.create({
          type: 'success',
          title: isEdit
            ? 'Forma de pagamento salva'
            : 'Forma de pagamento adicionada',
          description: `${value.code.trim()} · ${value.description.trim()}`,
        });
        onDone();
      } catch (error) {
        setServerError(
          error instanceof Error
            ? error.message
            : 'Não foi possível salvar a forma de pagamento.'
        );
      }
    },
  });

  const billingMethod = useStore(
    form.store,
    state => state.values.billingMethod
  );

  return {
    form,
    isEdit,
    isSaving: mutation.isPending,
    serverError,
    bankError,
    clearBankError: () => setBankError(undefined),
    requiresBank: billingMethod === BILLING_METHOD_REQUIRING_BANK,
  };
}
